import { useEffect, useRef, useState, type DragEvent, type KeyboardEvent } from 'react';
import { useT } from '../i18n/i18n';
import type { Session } from './api';
import { addDays, addMonths, byDay, daysBetween, mondayOf, monthEnd, monthStart, parseDay, today, useDates } from './dates';
import { SessionCard, SessionChip } from './SessionParts';

export type CalView = 'month' | 'week' | 'day' | 'list';

type Common = {
  anchor: string;
  sessions: Session[];
  onOpen: (s: Session) => void;
  onEdit: (s: Session) => void;
  onMove: (s: Session, day: string) => Promise<void> | void;
  onAdd: (day: string) => void;
  onDay: (day: string) => void;
};

export function viewRange(view: CalView, anchor: string): { from: string; to: string } {
  if (view === 'month') return { from: mondayOf(monthStart(anchor)), to: addDays(mondayOf(monthEnd(anchor)), 6) };
  if (view === 'week') return { from: mondayOf(anchor), to: addDays(mondayOf(anchor), 6) };
  if (view === 'day') return { from: anchor, to: anchor };
  return { from: monthStart(anchor), to: monthEnd(anchor) };
}

export function shiftAnchor(view: CalView, anchor: string, dir: 1 | -1): string {
  if (view === 'week') return addDays(anchor, 7 * dir);
  if (view === 'day') return addDays(anchor, dir);
  return addMonths(anchor, dir);
}

function useDrop(sessions: Session[], onMove: Common['onMove']) {
  const [over, setOver] = useState<string | null>(null);
  return {
    over,
    props: (day: string) => ({
      onDragOver: (e: DragEvent) => {
        if (!e.dataTransfer.types.includes('text/plain')) return;
        e.preventDefault();
        e.dataTransfer.dropEffect = 'move';
        if (over !== day) setOver(day);
      },
      onDragLeave: () => setOver((o) => (o === day ? null : o)),
      onDrop: (e: DragEvent) => {
        e.preventDefault();
        setOver(null);
        const s = sessions.find((x) => x.id === e.dataTransfer.getData('text/plain'));
        if (s && s.day !== day) void onMove(s, day);
      },
    }),
  };
}

export function MonthView({ anchor, setAnchor, sessions, onOpen, onMove, onDay, labelledBy }: Common & { setAnchor: (d: string) => void; labelledBy: string }) {
  const t = useT();
  const d = useDates();
  const grid = useRef<HTMLDivElement | null>(null);
  const wantFocus = useRef(false);
  const map = byDay(sessions);
  const { from, to } = viewRange('month', anchor);
  const days = daysBetween(from, to);
  const weeks: string[][] = [];
  for (let i = 0; i < days.length; i += 7) weeks.push(days.slice(i, i + 7));
  const month = parseDay(anchor).getMonth();
  const now = today();
  const drop = useDrop(sessions, onMove);

  useEffect(() => {
    if (!wantFocus.current) return;
    wantFocus.current = false;
    grid.current?.querySelector<HTMLElement>(`[data-day="${anchor}"]`)?.focus();
  }, [anchor]);

  function go(next: string) {
    wantFocus.current = true;
    setAnchor(next);
  }

  function onGridKey(e: KeyboardEvent<HTMLDivElement>) {
    const target = e.target as HTMLElement;
    if (target.getAttribute('role') !== 'gridcell') return;
    const rtl = document.documentElement.dir === 'rtl';
    const moves: Record<string, () => string> = {
      ArrowRight: () => addDays(anchor, rtl ? -1 : 1),
      ArrowLeft: () => addDays(anchor, rtl ? 1 : -1),
      ArrowDown: () => addDays(anchor, 7),
      ArrowUp: () => addDays(anchor, -7),
      Home: () => mondayOf(anchor),
      End: () => addDays(mondayOf(anchor), 6),
      PageUp: () => addMonths(anchor, -1),
      PageDown: () => addMonths(anchor, 1),
    };
    if (moves[e.key]) {
      e.preventDefault();
      go(moves[e.key]());
      return;
    }
    if (e.key === 'Enter' || e.key === ' ') {
      e.preventDefault();
      const chip = target.querySelector<HTMLElement>('[data-session]');
      if (chip) chip.focus();
      else onDay(anchor);
    }
  }

  function onChipKey(e: KeyboardEvent<HTMLButtonElement>) {
    const cell = e.currentTarget.closest<HTMLElement>('[role="gridcell"]');
    if (!cell) return;
    const chips = Array.from(cell.querySelectorAll<HTMLElement>('[data-session]'));
    const at = chips.indexOf(e.currentTarget);
    if (e.key === 'ArrowDown' || e.key === 'ArrowUp') {
      e.preventDefault();
      e.stopPropagation();
      chips[Math.max(0, Math.min(chips.length - 1, at + (e.key === 'ArrowDown' ? 1 : -1)))]?.focus();
    } else if (e.key === 'Escape') {
      e.preventDefault();
      e.stopPropagation();
      cell.focus();
    } else if (e.key.startsWith('Arrow') || e.key === 'Home' || e.key === 'End' || e.key.startsWith('Page')) {
      e.stopPropagation();
    }
  }

  return (
    <div ref={grid} role="grid" aria-labelledby={labelledBy} className="planner-cal-month" onKeyDown={onGridKey}>
      <div role="row" className="planner-cal-row planner-cal-headrow">
        {[1, 2, 3, 4, 5, 6, 7].map((n) => (
          <div role="columnheader" key={n} className="planner-cal-colhead">
            <abbr title={d.weekdayLong(n)}>{d.weekdayShort(n)}</abbr>
          </div>
        ))}
      </div>
      {weeks.map((w) => (
        <div role="row" className="planner-cal-row" key={w[0]}>
          {w.map((day, col) => {
            const list = map.get(day) ?? [];
            const outside = parseDay(day).getMonth() !== month;
            const cls = ['planner-cal-cell'];
            if (outside) cls.push('is-outside');
            if (day === now) cls.push('is-today');
            if (drop.over === day) cls.push('is-drop');
            return (
              <div
                role="gridcell"
                key={day}
                data-day={day}
                tabIndex={day === anchor ? 0 : -1}
                aria-selected={day === anchor}
                aria-current={day === now ? 'date' : undefined}
                className={cls.join(' ')}
                onClick={(e) => { if (e.target === e.currentTarget) go(day); }}
                {...drop.props(day)}
              >
                <span className="visually-hidden">{d.long(day)}</span>
                {day === now && <span className="visually-hidden">{t('Today')}</span>}
                <span className="planner-cal-daynum" aria-hidden="true">{d.dayNumber(day)}</span>
                {list.slice(0, 3).map((s) => (
                  <SessionChip key={s.id} session={s} onOpen={onOpen} onMove={onMove} draggable tabIndex={-1} align={col >= 4 ? 'end' : 'start'} onKey={onChipKey} />
                ))}
                {list.length > 3 && (
                  <button type="button" tabIndex={-1} className="btn ghost planner-cal-more" onClick={() => onDay(day)}>
                    {t('+{n} more', { n: list.length - 3 })}
                  </button>
                )}
                {list.length === 0 && <span className="visually-hidden">{t('Nothing planned')}</span>}
              </div>
            );
          })}
        </div>
      ))}
    </div>
  );
}

export function WeekView({ anchor, sessions, onOpen, onMove, onDay }: Common) {
  const t = useT();
  const d = useDates();
  const map = byDay(sessions);
  const { from, to } = viewRange('week', anchor);
  const now = today();
  const drop = useDrop(sessions, onMove);
  return (
    <div className="planner-cal-week">
      {daysBetween(from, to).map((day, col) => {
        const list = map.get(day) ?? [];
        const cls = ['planner-cal-col'];
        if (day === now) cls.push('is-today');
        if (drop.over === day) cls.push('is-drop');
        return (
          <section key={day} className={cls.join(' ')} aria-label={d.long(day)} {...drop.props(day)}>
            <h3 className="planner-cal-colhead">
              <button type="button" className="planner-cal-daylink" onClick={() => onDay(day)}>
                <span>{d.weekdayShort(col + 1)}</span> <span>{d.dayNumber(day)}</span>
              </button>
              {day === now && <span className="pill">{t('Today')}</span>}
            </h3>
            {list.length === 0 ? (
              <p className="muted planner-cal-empty">{t('Nothing planned')}</p>
            ) : (
              <ul className="planner-cal-chips">
                {list.map((s) => (
                  <li key={s.id}><SessionChip session={s} onOpen={onOpen} onMove={onMove} draggable align={col >= 4 ? 'end' : 'start'} /></li>
                ))}
              </ul>
            )}
          </section>
        );
      })}
    </div>
  );
}

export function DayView({ anchor, sessions, onOpen, onEdit, onMove, onAdd }: Common) {
  const t = useT();
  const list = byDay(sessions).get(anchor) ?? [];
  return (
    <div className="planner-cal-day">
      {list.length === 0 && <p className="muted">{t('Nothing planned for this day.')}</p>}
      {list.map((s) => <SessionCard key={s.id} session={s} onOpen={onOpen} onEdit={onEdit} onMove={onMove} />)}
      <div className="toolbar">
        <button type="button" className="btn secondary" onClick={() => onAdd(anchor)}>{t('Add a session on this day')}</button>
      </div>
    </div>
  );
}

export function ListView({ anchor, sessions, onOpen, onMove, onDay }: Common) {
  const t = useT();
  const d = useDates();
  const map = byDay(sessions);
  const now = today();
  const days = [...map.keys()];
  if (days.length === 0) return <p className="muted">{t('Nothing planned in {month}.', { month: d.month(anchor) })}</p>;
  return (
    <div className="planner-cal-list">
      {days.map((day) => (
        <section key={day} className={day === now ? 'planner-cal-listday is-today' : 'planner-cal-listday'}>
          <h3>
            <button type="button" className="planner-cal-daylink" onClick={() => onDay(day)}>{d.long(day)}</button>
            {day === now && <span className="pill">{t('Today')}</span>}
          </h3>
          <ul className="planner-cal-chips">
            {map.get(day)!.map((s) => (
              <li key={s.id}><SessionChip session={s} onOpen={onOpen} onMove={onMove} wide /></li>
            ))}
          </ul>
        </section>
      ))}
    </div>
  );
}
